import React, { useEffect, useState } from "react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import "../shared/styles/auth.css"
import OdontoFlowLogo from "../shared/brand/OdontoFlowLogo"

function normalizeError(err) {
  const message =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    "Credenciales inválidas"

  return Array.isArray(message) ? message[0] : message
}

function isEmailVerificationMessage(message) {
  const text = String(message || "").toLowerCase()
  return text.includes("confirma tu correo") || text.includes("cuenta aún no está activa")
}

export default function LoginPage() {
  const { login, isAuthenticated, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [verificationPending, setVerificationPending] = useState(false)
  const [resendMessage, setResendMessage] = useState("")
  const [debugVerificationUrl, setDebugVerificationUrl] = useState("")
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate("/agenda", { replace: true })
    }
  }, [authLoading, isAuthenticated, navigate])

  async function handleLogin(e) {
    e.preventDefault()

    try {
      setLoading(true)
      setError(null)
      setVerificationPending(false)
      setResendMessage("")
      setDebugVerificationUrl("")
      await login(email, password)
      navigate("/agenda", { replace: true })
    } catch (err) {
      const message = normalizeError(err)
      setError(message)
      setVerificationPending(isEmailVerificationMessage(message))
    } finally {
      setLoading(false)
    }
  }

  async function handleResendVerification() {
    const cleanEmail = email.trim().toLowerCase()
    if (!cleanEmail) {
      setError("Escribe tu correo para reenviar la confirmación")
      return
    }

    try {
      setResending(true)
      setError(null)
      setResendMessage("")
      setDebugVerificationUrl("")

      const { data } = await axios.post("/api/auth/resend-verification", {
        email: cleanEmail,
      })

      setResendMessage(data?.message || "Te enviamos un nuevo correo de confirmación.")
      setDebugVerificationUrl(data?.debugVerificationUrl || "")
    } catch (err) {
      setError(normalizeError(err))
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="of-auth-page of-auth-page--login-clean">
      <div className="of-auth-bg-glow of-auth-bg-glow-1" />
      <div className="of-auth-bg-glow of-auth-bg-glow-2" />

      <div className="of-auth-shell of-auth-shell--login-clean">
        <section className="of-auth-brand of-auth-brand--login-clean">
          <div className="of-auth-brand-top">
            <div className="of-auth-logo-row">
              <OdontoFlowLogo
                className="of-auth-login-logo"
                theme="dark"
                size="xl"
                showDescriptor
                descriptor="Operación, agenda y cobranza en una sola plataforma."
                style={{
                  maxWidth: 460,
                  width: "100%",
                }}
              />
            </div>
          </div>
        </section>

        <section className="of-auth-panel">
          <div className="of-auth-form-wrap">
            <div className="of-auth-form-card">
              <div className="of-auth-form-header">
                <h2>Bienvenido</h2>
              </div>

              {error ? <div className="of-auth-error">{error}</div> : null}

              {resendMessage ? (
                <div className="of-auth-helper" style={{ color: "#166534" }}>
                  {resendMessage}
                </div>
              ) : null}

              {debugVerificationUrl ? (
                <div className="of-auth-error" style={{ background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}>
                  Modo local/debug: <br />
                  <a href={debugVerificationUrl} style={{ color: "#1d4ed8", fontWeight: 900, wordBreak: "break-all" }}>
                    Abrir liga de verificación
                  </a>
                </div>
              ) : null}

              <form onSubmit={handleLogin} className="of-auth-form">
                <div className="of-auth-field">
                  <label>Correo</label>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="tu@clinica.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="of-auth-field">
                  <label>Contraseña</label>

                  <div className="of-auth-password-wrap">
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />

                    <button
                      type="button"
                      className="of-auth-password-toggle"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? "Ocultar" : "Ver"}
                    </button>
                  </div>
                </div>

                <button
                  className="of-auth-submit"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Entrando..." : "Entrar"}
                </button>
              </form>

              <div className="of-auth-secondary-actions">
                {verificationPending ? (
                  <button
                    type="button"
                    className="of-auth-link-button"
                    onClick={handleResendVerification}
                    disabled={resending}
                  >
                    {resending ? "Reenviando..." : "Reenviar correo de confirmación"}
                  </button>
                ) : null}

                <button
                  type="button"
                  className="of-auth-link-button"
                  onClick={() => navigate("/register")}
                >
                  Crear cuenta para mi clínica
                </button>

                <button
                  type="button"
                  className="of-auth-muted-button"
                  onClick={() => alert("Recuperación de contraseña pendiente. Por ahora contacta al administrador de OdontoFlow.")}
                >
                  Olvidé mi contraseña
                </button>
              </div>
</div>
          </div>
        </section>
      </div>
    </div>
  )
}
